import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useLanguage } from "../../../../../context/LanguageContext";
import { useAdminProductsTranslation } from "../../translations";
import { isFeatureEnabled } from "../../../../../config/features";
import AIImageEditor from "../../../../../components/AIImageEditor/AIImageEditor";

const ImagesTab = ({
    product,
    setProduct,
    selectedFiles,
    setSelectedFiles,
    activeTab
}) => {
    const { language } = useLanguage();
    const { t } = useAdminProductsTranslation(language);
    const [dragActive, setDragActive] = useState(false);
    const [uploadMode, setUploadMode] = useState('standard');

    const aiFeatureEnabled = isFeatureEnabled('AI_IMAGE_EDITING');

    const isValidFile = useCallback((file) => {
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        const maxSize = 10 * 1024 * 1024;
        return validTypes.includes(file.type) && file.size <= maxSize;
    }, []);

    const handleFileSelect = useCallback((event) => {
        const files = Array.from(event.target.files || []);
        const validFiles = files.filter(isValidFile);
        if (validFiles.length > 0) {
            setSelectedFiles(prev => [...prev, ...validFiles]);
        }
        event.target.value = '';
    }, [isValidFile, setSelectedFiles]);

    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        const files = Array.from(e.dataTransfer.files || []);
        const imageFiles = files.filter(file => file.type.startsWith('image/'));
        const validFiles = imageFiles.filter(isValidFile);
        if (validFiles.length > 0) {
            setSelectedFiles(prev => [...prev, ...validFiles]);
        }
    }, [isValidFile, setSelectedFiles]);

    const removeFile = useCallback((index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    }, [setSelectedFiles]);

    const removeProductImage = useCallback((index) => {
        if (window.confirm('Are you sure you want to delete this image?')) {
            const updatedImages = product.images.filter((_, i) => i !== index);
            setProduct({ ...product, images: updatedImages });
        }
    }, [product, setProduct]);

    const previewUrls = useMemo(() => {
        return selectedFiles.map(file => {
            try {
                return URL.createObjectURL(file);
            } catch (error) {
                console.error('Failed to create preview URL:', error);
                return null;
            }
        });
    }, [selectedFiles]);

    useEffect(() => {
        return () => {
            previewUrls.forEach(url => {
                if (url) URL.revokeObjectURL(url);
            });
        };
    }, [previewUrls]);

    const totalImages = (product.images?.length || 0) + selectedFiles.length;
    const hasImages = totalImages > 0;

    const handleAIImageGenerated = useCallback((imageUrl) => {
        fetch(imageUrl)
            .then(res => res.blob())
            .then(blob => {
                const file = new File([blob], `ai-generated-${Date.now()}.png`, { type: 'image/png' });
                setSelectedFiles(prev => [...prev, file]);
            })
            .catch(error => {
                console.error('Error converting AI image:', error);
            });
    }, [setSelectedFiles]);

    if (!aiFeatureEnabled) {
        return (
            <div className={`editor-tab-content ${activeTab === "images" ? "active" : ""}`}>
                <div className="editor-form-section">
                    <div className="images-tab-header">
                        <h2>{t('productImages')}</h2>
                        <p className="editor-form-help">
                            Upload high-quality images of your product.
                        </p>
                    </div>
                    <StandardImageUpload
                        dragActive={dragActive}
                        handleDragEnter={handleDragEnter}
                        handleDragLeave={handleDragLeave}
                        handleDragOver={handleDragOver}
                        handleDrop={handleDrop}
                        handleFileSelect={handleFileSelect}
                        product={product}
                        removeProductImage={removeProductImage}
                        selectedFiles={selectedFiles}
                        previewUrls={previewUrls}
                        removeFile={removeFile}
                        hasImages={hasImages}
                        totalImages={totalImages}
                        t={t}
                    />
                    <div className="image-guidelines">
                        <h4>Image Guidelines</h4>
                        <ul>
                            <li>Use high-quality images</li>
                            <li>Show your product from different angles</li>
                            <li>The first image will be the main product image</li>
                        </ul>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`editor-tab-content ${activeTab === "images" ? "active" : ""}`}>
            <div className="editor-form-section">
                <div className="images-tab-header">
                    <h2>{t('productImages')}</h2>
                    <p className="editor-form-help">
                        Choose between standard upload or AI-powered image editing.
                    </p>
                </div>
                <div className="upload-mode-toggle" style={{ marginBottom: '20px' }}>
                    <button
                        type="button"
                        onClick={() => setUploadMode('standard')}
                        style={{
                            padding: '10px 20px',
                            marginRight: '10px',
                            border: '2px solid',
                            borderColor: uploadMode === 'standard' ? '#8C1C13' : '#ccc',
                            backgroundColor: uploadMode === 'standard' ? '#8C1C13' : 'white',
                            color: uploadMode === 'standard' ? 'white' : '#333',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            transition: 'all 0.3s'
                        }}
                    >
                        {t('standardImageUpload')}
                    </button>
                    <button
                        type="button"
                        onClick={() => setUploadMode('ai')}
                        style={{
                            padding: '10px 20px',
                            border: '2px solid',
                            borderColor: uploadMode === 'ai' ? '#8C1C13' : '#ccc',
                            backgroundColor: uploadMode === 'ai' ? '#8C1C13' : 'white',
                            color: uploadMode === 'ai' ? 'white' : '#333',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            transition: 'all 0.3s'
                        }}
                    >
                        ✨ {t('aiImageUpload')}
                    </button>
                </div>
                {uploadMode === 'standard' ? (
                    <StandardImageUpload
                        dragActive={dragActive}
                        handleDragEnter={handleDragEnter}
                        handleDragLeave={handleDragLeave}
                        handleDragOver={handleDragOver}
                        handleDrop={handleDrop}
                        handleFileSelect={handleFileSelect}
                        product={product}
                        removeProductImage={removeProductImage}
                        selectedFiles={selectedFiles}
                        previewUrls={previewUrls}
                        removeFile={removeFile}
                        hasImages={hasImages}
                        totalImages={totalImages}
                        t={t}
                    />
                ) : (
                    <div className="ai-image-editor-container">
                        <AIImageEditor onImageGenerated={handleAIImageGenerated} />
                    </div>
                )}
                <div className="image-guidelines">
                    <h4>Image Guidelines</h4>
                    <ul>
                        <li>Use high-quality images</li>
                        <li>Show your product from different angles</li>
                        <li>The first image will be the main product image</li>
                        {uploadMode === 'ai' && <li>AI Editor: Upload clothing images for professional photos</li>}
                    </ul>
                </div>
            </div>
        </div>
    );
};

const StandardImageUpload = ({
    dragActive, handleDragEnter, handleDragLeave, handleDragOver,
    handleDrop, handleFileSelect, product, removeProductImage,
    selectedFiles, previewUrls, removeFile, hasImages, totalImages, t
}) => {
    return (
        <>
            <div className={`image-upload-area ${dragActive ? 'drag-active' : ''}`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
            >
                <input type="file" id="product-images" multiple accept="image/*"
                    onChange={handleFileSelect} className="file-input" style={{ display: 'none' }} />
                <label htmlFor="product-images" className="upload-label">
                    <div className="upload-content">
                        <div className="upload-icon">📷</div>
                        <h3>{t('uploadImages')}</h3>
                        <p>{t('dragDropImages')}</p>
                        <div className="upload-specs">
                            <span>{t('supportedFormats')}</span>
                        </div>
                    </div>
                </label>
                {dragActive && <div className="drag-overlay"><div className="drag-message"><span>Drop images here</span></div></div>}
            </div>
            {hasImages ? (
                <div className="images-management">
                    <h3>{t('productImages')} ({totalImages})</h3>
                    {product.images && product.images.length > 0 && (
                        <div className="existing-images-section">
                            <h4>Current Images</h4>
                            <div className="images-grid">
                                {product.images.map((image, index) => (
                                    <div key={`existing-${index}`} className="image-item">
                                        <div className="image-container">
                                            <img src={image} alt={`Product ${index + 1}`} />
                                            {index === 0 && <span className="main-badge">{t('mainImage')}</span>}
                                            <button type="button" className="remove-btn"
                                                onClick={() => removeProductImage(index)} title="Delete image">✕</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {selectedFiles.length > 0 && (
                        <div className="new-images-section">
                            <h4>New Images ({selectedFiles.length})</h4>
                            <div className="images-grid">
                                {selectedFiles.map((file, index) => (
                                    <div key={`new-${index}`} className="image-item">
                                        <div className="image-container">
                                            {previewUrls[index] ? (
                                                <img src={previewUrls[index]} alt={`New ${index + 1}`} />
                                            ) : (
                                                <div className="image-placeholder"><span>Preview unavailable</span></div>
                                            )}
                                            {(product.images?.length || 0) + index === 0 && <span className="main-badge">Main</span>}
                                            <button type="button" className="remove-btn"
                                                onClick={() => removeFile(index)} title="Remove image">✕</button>
                                        </div>
                                        <div className="image-info">
                                            <span className="file-name">{file.name}</span>
                                            <span className="file-size">{(file.size / 1024).toFixed(1)}KB</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="empty-images-state">
                    <div className="empty-icon">🖼️</div>
                    <h3>No Images Yet</h3>
                    <p>Start by uploading some product images.</p>
                </div>
            )}
        </>
    );
};

export default ImagesTab;
